import { useState } from 'react';
import { Button, Modal } from '@/components/common';
import { usePermission } from '@/hooks/usePermission';
import { useScreeningAction, errorMessage } from './api';
import {
  canDecide,
  canResubmit,
  validReason,
  type ScreeningDetail,
  type DecisionBody,
} from './types';
import { Section, fieldClass } from './shared';

export function ScreeningActions({ row }: { row: ScreeningDetail }) {
  const { has } = usePermission();
  const action = useScreeningAction(row.screening_id);
  const [dialog, setDialog] = useState<'decision' | 'resubmit' | null>(null);
  const [decision, setDecision] = useState<DecisionBody['decision'] | ''>('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [success, setSuccess] = useState('');
  const run = async (kind: 'sync' | 'decision' | 'resubmit') => {
    setSuccess('');
    try {
      await action.mutateAsync({
        action: kind,
        body: kind === 'decision' && decision ? { decision, reason: reason.trim() } : undefined,
      });
      setDialog(null);
      setReason('');
      setConfirmed(false);
      setDecision('');
      setSuccess(
        kind === 'resubmit'
          ? 'Screening returned to pending. Provider submission and evaluation are not confirmed.'
          : kind === 'sync'
            ? 'Provider results fetched from the screening provider.'
            : 'Screening decision recorded. This does not confirm financial execution.',
      );
    } catch {
      /* The mutation error is shown below, including backend conflict/service status. */
    }
  };
  const open = (kind: 'decision' | 'resubmit') => {
    action.reset();
    setSuccess('');
    setDialog(kind);
    setConfirmed(false);
  };
  return (
    <Section title="Actions">
      <div className="flex flex-wrap gap-3">
        {has('COMPLIANCE_VIEW') && (
          <Button
            variant="outline"
            disabled={!row.provider_ref || action.isPending}
            onClick={() => void run('sync')}
            aria-describedby="refresh-help"
          >
            Refresh provider results
          </Button>
        )}
        {has('COMPLIANCE_DECIDE') && canDecide(row) && (
          <Button disabled={action.isPending} onClick={() => open('decision')}>
            Record manual decision
          </Button>
        )}
        {has('COMPLIANCE_DECIDE') && canResubmit(row) && (
          <Button variant="outline" disabled={action.isPending} onClick={() => open('resubmit')}>
            Resubmit corrected request
          </Button>
        )}
      </div>
      <p id="refresh-help" className="mt-3 text-xs text-[#777]">
        Refresh provider results fetches the latest stored dossier from the screening provider. It
        does not resubmit the transaction. Reloading the browser page only re-reads what the CBS has
        already recorded.
      </p>
      <p className="mt-2 text-xs text-[#777]">
        Actions remain subject to backend validation, including applied and terminal transaction
        protections.
      </p>
      {!row.provider_ref && (
        <p className="mt-2 text-sm text-[#777]">Refresh requires an existing provider reference.</p>
      )}
      {row.status === 'hold' && row.hold_reason === 'rules' && (
        <p className="mt-3 text-sm">
          Rules holds require review and a decision in the Didit console. An approved console link
          is not available.
        </p>
      )}
      {action.isPending && (
        <p role="status" className="mt-3 text-sm">
          Submitting request…
        </p>
      )}
      {success && (
        <p role="status" className="mt-3 text-sm text-[#2e7d32]">
          {success}
        </p>
      )}
      {!dialog && action.isError && (
        <p role="alert" className="mt-3 text-sm text-[#c62828]">
          {errorMessage(action.error)}
        </p>
      )}
      <Modal
        isOpen={dialog !== null}
        title={dialog === 'decision' ? 'Manual screening decision' : 'Resubmit corrected request'}
        onClose={() => {
          if (!action.isPending) setDialog(null);
        }}
      >
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (dialog && confirmed && (dialog === 'resubmit' || (decision && validReason(reason))))
              void run(dialog);
          }}
        >
          <p className="break-words text-sm">
            Transaction <strong>{row.transaction_id}</strong> · Screening {row.screening_id}
          </p>
          {dialog === 'decision' ? (
            <>
              <label className="block text-sm">
                Decision
                <select
                  required
                  value={decision}
                  onChange={(e) => setDecision(e.target.value as DecisionBody['decision'] | '')}
                  className={fieldClass}
                >
                  <option value="">Select a decision</option>
                  <option value="approve">Approve</option>
                  <option value="decline">Decline</option>
                </select>
              </label>
              <label className="block text-sm">
                Reason
                <textarea
                  required
                  className={fieldClass}
                  rows={4}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  aria-describedby="reason-help"
                />
              </label>
              <p id="reason-help" className="text-xs text-[#777]">
                10–1000 characters after trimming. {reason.trim().length}/1000
              </p>
              {reason.length > 0 && !validReason(reason) && (
                <p className="text-sm text-[#c62828]">
                  Enter a reason of 10–1000 characters after trimming.
                </p>
              )}
            </>
          ) : (
            <p className="text-sm">
              Confirm the provider-rejected request has been corrected. Resubmission returns
              screening to pending; it does not prove successful provider submission or evaluation.
            </p>
          )}
          <label className="flex items-start gap-2 break-words text-sm">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1"
            />{' '}
            <span>
              I confirm this {dialog === 'decision' ? 'decision' : 'resubmission'} for transaction{' '}
              {row.transaction_id}.
            </span>
          </label>
          {action.isError && (
            <p role="alert" className="text-sm text-[#c62828]">
              {errorMessage(action.error)}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={action.isPending}
              onClick={() => setDialog(null)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                action.isPending ||
                !confirmed ||
                (dialog === 'decision' && (!decision || !validReason(reason)))
              }
            >
              {action.isPending ? 'Submitting…' : 'Confirm'}
            </Button>
          </div>
        </form>
      </Modal>
    </Section>
  );
}
